import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { T, t } from '@sm/intl';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Grid } from '@wds/grid';
import { Typography } from '@wds/typography';
import { IconMoreAndroid } from '@wds/icons';
import { Menu } from '@wds/menu';
import { useFilters, useFiltersActions, useThemes, useTrainingActions, useRetraining, useResponses } from '../../Context';
import OpenEndedCodingContext from '../../OpenEndedCodingContext';
import { COLD_START_ENABLED, MIN_NUMBER_OF_THEMES } from '../../constants';
import FiveHundredError from '../../shared/FiveHundredError';
import LinkStyleButton from '../../shared/LinkStyleButton';
import { OECCreatedSource, OECTrainingStatus } from '../../types';
import AddThemeModal from './AddThemeModal';
import AddThemeModalColdStart from './AddThemeModalColdStart';
import DeleteThemeModal from './DeleteThemeModal';
import EditThemeModal from './EditThemeModal';
import ChangeUseCaseModal from './ChangeUseCaseModal';
import ThemeSection from './ThemeSection';
import ThemesSkeleton from './ThemesSkeleton';
import UseCaseSelector from './UseCaseSelector';
import SentimentStats from './SentimentStats';
import { TRAINING_STEP } from '../../types';
import { mergeSelectedIdsAndAutoGeneratedIds, transformKeywords } from '../../lib';
export const COPY = {
  title: {
    id: 'OpenEndedCoding.ThemeSelector.title',
    defaultMessage: 'Themes',
    description: '[Type: Header][Vis: med] - Header for theme selection'
  },
  emptyStateRepeatedly: {
    id: 'OpenEndedCoding.ThemeSelector.emptyStateRepeatedly',
    defaultMessage: 'As you read your responses, you’ll probably notice some topics, or <strong>themes</strong>, come up repeatedly.',
    description: '[Type: Header][Vis: med] - empty state theme selection'
  },
  emptyStateThematic: {
    id: 'OpenEndedCoding.ThemeSelector.emptyStateThematic',
    defaultMessage: 'These themes can reveal important concepts and patterns in your data. <strong>Thematic analysis</strong> is the process of identifying what these themes are and where they appear.',
    description: '[Type: Header][Vis: med] - empty state theme selection'
  },
  button: {
    id: 'OpenEndedCoding.ThemeSelector.button',
    defaultMessage: 'New theme',
    description: '[Type: Header][Vis: med] - button text theme selection'
  },
  error: {
    id: 'OpenEndedCoding.ThemeSelector.error',
    defaultMessage: "We couldn't load your themes",
    description: '[Type: Header][Vis: med] - OEC load themes error'
  },
  iconMoreAndroid: {
    id: 'OpenEndedCoding.ThemeSelector.iconMoreAndroid',
    defaultMessage: 'theme options',
    description: '[Type: Label][Vis: low] - icon to show view and edit keywords'
  },
  viewAndEditKeywords: {
    id: 'OpenEndedCoding.ThemeSelector.viewAndEditKeywords',
    defaultMessage: 'View & edit keywords',
    description: '[Type: Header][Vis: med] - View and edit keywords menu item'
  }
};
const useStyles = createUseStyles(() => {
  // We want to ensure the modal has a fixed height.
  // and doesn't increase in size once responses are loaded.
  return {
    container: {
      height: '90%',
      display: 'flex',
      flexDirection: 'column'
    },
    bottom: {
      flexGrow: 0,
      flexShrink: 0
    },
    body: {
      flexGrow: 1
    }
  };
});
const ThemeSelector = ({
  createdSource,
  showDeleteThemeModal,
  showEditThemeModal,
  showCreateThemeModal,
  showChangeUseCaseModal,
  setShowDeleteThemeModal,
  setShowEditThemeModal,
  setShowCreateThemeModal,
  setShowChangeUseCaseModal,
  setCreatedSource
}) => {
  const classes = useStyles();
  const {
    deleteThemeHandler,
    updateThemeHandler,
    createThemeHandler,
    getKeywords,
    filterResponses
  } = useContext(OpenEndedCodingContext);
  const {
    keywords,
    themes,
    themesLoading,
    themesError
  } = useThemes();
  const {
    selectedThemes,
    filterByTaggedStatus,
    sortValue,
    searchInput
  } = useFilters();
  const {
    setSelectedThemes
  } = useFiltersActions();
  const {
    setShowTrainingScreen,
    setMultiple: setMultipleTrainingFields
  } = useTrainingActions();
  const {
    trainingStatus
  } = useRetraining();
  const {
    showSA
  } = useResponses();
  const [selectedTheme, setSelectedTheme] = useState();
  const [previousUseCase, setPreviousUseCase] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [userAction, setUserAction] = useState(false);
  // execute the filter on user actions
  useEffect(() => {
    if (userAction) {
      filterResponses({
        themeIds: mergeSelectedIdsAndAutoGeneratedIds(themes, selectedThemes),
        filterByTaggedStatus: filterByTaggedStatus,
        sortBy: sortValue,
        search: searchInput
      });
      setUserAction(false);
    }
  }, [selectedThemes, userAction]);
  const onDelete = () => {
    if (selectedTheme) {
      deleteThemeHandler(selectedTheme);
    }
    setSelectedThemes(selectedThemes.filter(tid => tid !== selectedTheme));
    setUserAction(true);
    setSelectedTheme(undefined);
  };
  const onSave = name => {
    // Update the name of the theme
    if (selectedTheme) {
      updateThemeHandler(selectedTheme, name);
    }
    setSelectedTheme(undefined);
  };
  const onCreateTheme = name => {
    createThemeHandler(name);
    setSelectedTheme(undefined);
  };
  // need to fetch keywords when user opens modal
  const onClickEdit = themeId => {
    getKeywords();
    setSelectedTheme(themeId);
    setShowEditThemeModal(true);
  };
  const CreateThemeButton = () => _jsx(Button, {
    color: "alt",
    onClick: () => {
      setShowCreateThemeModal(true);
      setCreatedSource(OECCreatedSource.THEMES);
    },
    size: "sm",
    children: _jsx(T, {
      desc: COPY.button
    })
  });
  const handleClickViewAndEditKeywords = () => {
    setShowMenu(false);
    setShowTrainingScreen(true);
    setMultipleTrainingFields({
      trainingStep: TRAINING_STEP.KEYWORDS_VIEWING,
      unsavedKeywords: transformKeywords({
        keywords
      })
    });
  };
  const viewAndEditKeywordsTrigger = _jsx(Box, {
    "data-testid": "ViewAndEditKeywordsButton",
    children: _jsx(LinkStyleButton, {
      children: _jsx(IconMoreAndroid, {
        title: t(COPY.iconMoreAndroid),
        color: "darkMuted"
      })
    })
  });
  const ThemesComponent = () => {
    if (themesLoading) {
      return _jsx(ThemesSkeleton, {});
    }
    if (themesError) {
      return _jsx(FiveHundredError, {
        title: t(COPY.error)
      });
    }
    return _jsx(_Fragment, {
      children: _jsx(ThemeSection, {
        onClickDelete: themeId => {
          setSelectedTheme(themeId);
          setShowDeleteThemeModal(true);
        },
        onClickEdit: onClickEdit
      })
    });
  };
  return _jsxs(_Fragment, {
    children: [_jsxs(Box, {
      py: 5,
      pl: 6,
      pr: 4,
      className: classes.container,
      children: [_jsxs(Box, {
        className: classes.body,
        children: [_jsxs(Grid, {
          noGutters: true,
          noBleeds: true,
          fluid: true,
          children: [_jsx(Grid.Item, {
            xs: 5,
            children: _jsx(Box, {
              children: _jsx(Typography, {
                component: "h5",
                variant: "cardTitle",
                children: _jsx("strong", {
                  children: _jsx(T, {
                    desc: COPY.title
                  })
                })
              })
            })
          }), _jsx(Grid.Item, {
            xs: 7,
            children: _jsxs(Box, {
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              children: [!themesLoading && themes.length !== 0 && _jsx(CreateThemeButton, {}), !themesLoading && themes.length >= MIN_NUMBER_OF_THEMES && trainingStatus !== OECTrainingStatus.NOT_TRAINED && _jsx(Box, {
                ml: 2,
                children: _jsx(Menu, {
                  placement: "bottom-right",
                  show: showMenu,
                  menuTrigger: viewAndEditKeywordsTrigger,
                  children: _jsx(Menu.Item, {
                    onClick: handleClickViewAndEditKeywords,
                    children: _jsx(T, {
                      desc: COPY.viewAndEditKeywords
                    })
                  })
                })
              })]
            })
          })]
        }), _jsx(Box, {
          mb: 3,
          children: ThemesComponent()
        }), !themesLoading && themes.length === 0 && _jsx(Box, {
          mb: 5,
          children: _jsx(CreateThemeButton, {})
        }), COLD_START_ENABLED && trainingStatus === OECTrainingStatus.NOT_TRAINED && _jsx(Box, {
          mb: 5,
          children: _jsx(UseCaseSelector, {
            setPreviousUseCase: setPreviousUseCase,
            setShowChangeUseCaseModal: setShowChangeUseCaseModal
          })
        })]
      }), showSA && _jsx(Box, {
        className: classes.bottom,
        children: _jsx(SentimentStats, {})
      })]
    }), _jsx(DeleteThemeModal, {
      show: showDeleteThemeModal,
      onClose: () => {
        setShowDeleteThemeModal(false);
        setSelectedTheme(undefined);
      },
      onDelete: onDelete
    }), _jsx(ChangeUseCaseModal, {
      show: showChangeUseCaseModal,
      previousUseCase: previousUseCase,
      onClose: () => {
        setShowChangeUseCaseModal(false);
      }
    }), _jsx(EditThemeModal, {
      show: showEditThemeModal,
      selectedThemeId: selectedTheme,
      onClose: () => {
        setShowEditThemeModal(false);
        setSelectedTheme(undefined);
      },
      onSave: onSave
    }), COLD_START_ENABLED ? _jsx(AddThemeModalColdStart, {
      show: showCreateThemeModal,
      createdSource: createdSource,
      onClose: () => {
        setShowCreateThemeModal(false);
      }
    }) : _jsx(AddThemeModal, {
      show: showCreateThemeModal,
      createdSource: createdSource,
      selectedThemeId: selectedTheme,
      onClose: () => {
        setShowCreateThemeModal(false);
        setSelectedTheme(undefined);
      },
      onCreate: onCreateTheme
    })]
  });
};
export default ThemeSelector;